import { Link, useNavigate } from "@tanstack/react-router";
import type { NavigationItem } from "./navigation";
import { cn } from "@/shared/lib/cn";
import { NavigationItemContent } from "./NavigationItemContent";
import { withTooltip } from "./navigationUtils";

interface NavigationItemLinkProps {
  item: NavigationItem;
  collapsed: boolean;
  onNavigate?: () => void;
  iconNode: React.ReactNode;
}

export function NavigationItemLink({
  item,
  collapsed,
  onNavigate,
  iconNode,
}: NavigationItemLinkProps) {
  const isAdminRoute = item.target.to === "/admin";
  const navigate = useNavigate();

  const search = isAdminRoute
    ? item.target.search || {}
    : item.target.search || {};

  const handleClick = (e: React.MouseEvent) => {
    if (isAdminRoute && onNavigate) {
      e.preventDefault();
      const searchParams: Record<string, string | undefined> = {};
      if (item.target.search) {
        Object.entries(item.target.search).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams[key] = String(value);
          }
        });
      }
      navigate({
        to: item.target.to,
        params: item.target.params,
        search: searchParams,
        replace: true,
      });
      onNavigate();
    } else if (onNavigate) {
      onNavigate();
    }
  };

  const baseClasses =
    "group relative flex items-center gap-3 overflow-hidden rounded-2xl px-3 py-2 text-sm transition-all duration-200";
  const collapseAdjustment = collapsed ? "justify-center px-2" : "";

  const linkSearchParams: Record<string, string | undefined> = {};
  if (search) {
    Object.entries(search).forEach(([key, value]) => {
      if (value !== undefined) {
        linkSearchParams[key] = String(value);
      }
    });
  }

  const navLink = (
    <Link
      key={item.id}
      to={item.target.to}
      params={item.target.params}
      search={linkSearchParams}
      onClick={handleClick}
      className={cn(
        baseClasses,
        "border border-transparent bg-white/5 font-medium text-white/80",
        "hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10",
        "focus:outline-none focus-visible:outline-none",
        "data-[status=active]:border-imperial-500/70 data-[status=active]:bg-imperial-500/20 data-[status=active]:text-white data-[status=active]:shadow-telemetry",
        collapseAdjustment
      )}
    >
      <NavigationItemContent
        collapsed={collapsed}
        item={item}
        iconNode={iconNode}
      />
      <span className="absolute inset-y-0 left-0 w-[3px] rounded-r-full bg-linear-to-b from-imperial-400 via-imperial-500 to-imperial-600 opacity-0 transition-opacity duration-200 group-data-[status=active]:opacity-100" />
    </Link>
  );

  return collapsed ? withTooltip(collapsed, item, navLink) : navLink;
}
